import {
  Text,
  Button,
  useModalState,
  Modal,
  InputSelectControlField,
  Primary13Regular,
  Primary13SemiBold,
  Flex
} from '@sequencehq/core-components'
import { useState, useMemo, useCallback } from 'react'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

interface Props {
  taxCategoryName: string
  currentNumeralCategory: string
  currentNumeralCategoryTitle: string
  onUpdateMapping: (values: { numeralTaxCategory: string }) => Promise<void>
  trigger: React.ReactNode
}

const EditNumeralMappingModalUI = ({
  taxCategoryName,
  currentNumeralCategory,
  onUpdateMapping,
  onClose
}: Props & { onClose: () => void }) => {
  const { fields, queries } = useForm({
    value: {
      numeralTaxCategory: currentNumeralCategory // Pre-populate with current value
    },
    fieldConfiguration: [
      {
        property: 'numeralTaxCategory',
        validation: [required]
      }
    ]
  })

  // Fetch Numeral tax categories from API
  const {
    data: numeralTaxCategoriesData,
    isLoading: isLoadingCategories,
    isError: hasError
  } = useQuery(dashboardv99990101Client.getNumeralTaxCategories)

  const numeralTaxCategories = numeralTaxCategoriesData?.items || []
  const comboBoxOptions = useMemo(
    () =>
      numeralTaxCategories.map(category => ({
        value: category,
        label: category
      })),
    [numeralTaxCategories]
  )

  const [selectedValue, setSelectedValue] = useState<string>(
    currentNumeralCategory
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const canSubmit =
    queries.isValid &&
    selectedValue &&
    selectedValue !== currentNumeralCategory &&
    !isLoadingCategories &&
    !isSubmitting

  const handleChange = useCallback(
    (value: string) => {
      setSelectedValue(value)
      fields.numeralTaxCategory.onChange(value)
    },
    [fields.numeralTaxCategory]
  )

  const handleSubmit = useCallback(async () => {
    if (selectedValue && selectedValue !== currentNumeralCategory) {
      setIsSubmitting(true)
      try {
        await onUpdateMapping({
          numeralTaxCategory: selectedValue
        })
        onClose()
      } catch (error) {
        console.error('Failed to update mapping:', error)
      } finally {
        setIsSubmitting(false)
      }
    }
  }, [selectedValue, currentNumeralCategory, onUpdateMapping, onClose])

  return (
    <>
      <Modal.Header showCloseButton withBorder onClose={onClose}>
        Edit Numeral mapping
      </Modal.Header>
      <Modal.Content minHeight="auto">
        <Flex flexDirection="column" gap={3}>
          <Flex flexDirection="column" gap={2}>
            <Text {...Primary13Regular}>Mapping for:</Text>
            <Text as="span" {...Primary13SemiBold}>
              {taxCategoryName}
            </Text>
          </Flex>
          {hasError && (
            <div
              style={{ color: 'red', fontSize: '14px', marginBottom: '8px' }}
              data-testid="editNumeralMapping.error"
            >
              Failed to load Numeral tax categories. Please try again.
            </div>
          )}
          <InputSelectControlField
            label="Numeral tax category"
            placeholder={
              isLoadingCategories
                ? 'Loading categories...'
                : 'Select a tax category...'
            }
            options={comboBoxOptions}
            initialValue={selectedValue}
            onChange={handleChange}
            disabled={isLoadingCategories}
            data-testid="numeralTaxCategoryComboBox"
          />
        </Flex>
      </Modal.Content>
      <Modal.Footer withBorder>
        <Button
          onClick={onClose}
          style={{ marginLeft: 'auto' }}
          variant="secondary"
          disabled={isSubmitting}
          data-testid="editNumeralMapping.cancel"
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          type="button"
          disabled={!canSubmit || isSubmitting}
          onClick={handleSubmit}
          data-testid="editNumeralMapping.submit"
        >
          {isSubmitting ? 'Updating...' : 'Update mapping'}
        </Button>
      </Modal.Footer>
    </>
  )
}

const EditNumeralMappingModal = ({ ...props }: Props) => {
  const { isModalOpen, openModal, closeModal } = useModalState()

  return (
    <>
      <div onClick={openModal}>{props.trigger}</div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        data-testid="editNumeralMappingModal"
      >
        <EditNumeralMappingModalUI {...props} onClose={closeModal} />
      </Modal>
    </>
  )
}

export default EditNumeralMappingModal
